import React, {Suspense, lazy, useEffect, useState} from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';

// Core components that should load immediately
//import Navbar from "./components/Navbar";
//import Footer from "./components/Footer";
import LoadingSpinner from "./components/LoadingSpinner"; // You'll need to create this
import ErrorBoundary from "./components/ErrorBoundary";

// Lazy load components for better performance
const Hero = lazy(() => import("./components/Hero"));
const HeroMKD = lazy(() => import("./components/HeroMKD"));
const HeroNL = lazy(() => import("./components/HeroNL"));
const Navbar = lazy(() => import("./components/Navbar"));
const NavbarMKD = lazy(() => import("./components/NavbarMKD"));
const NavbarNL = lazy(() => import("./components/NavbarNL"));
const Footer = lazy(() => import("./components/Footer"));
const FooterNL = lazy(() => import("./components/FooterNL"));
const FooterMKD = lazy(() => import("./components/FooterMKD"));
const AtmosphereSection = lazy(() => import("./components/AtmosphereSection"));
const AtmosphereSectionNL = lazy(() => import("./components/AtmosphereSectionNL"));
const AtmosphereSectionMKD = lazy(() => import("./components/AtmosphereSectionMKD"));
const Testimonials = lazy(() => import("./components/Testimonials"));
const TestimonialsNL = lazy(() => import("./components/TestimonialsNL"));
const TestimonialsMKD = lazy(() => import("./components/TestimonialsMKD"));
const Reservation = lazy(() => import("./components/Reservation"));
const ReservationNL = lazy(() => import("./components/ReservationNL"));
const ReservationMKD = lazy(() => import("./components/ReservationMKD"));
const ResMenu = lazy(() => import("./components/ResMenu"));
const ResMenuNL = lazy(() => import("./components/ResMenuNL"));
const ResMenuMKD = lazy(() => import("./components/ResMenuMKD"));
const EventPost = lazy(() => import("./components/EventPost"));
const EventPostMKD = lazy(() => import("./components/EventPostMKD"));
const EventPostNL = lazy(() => import("./components/EventPostNL"));
const AboutUs = lazy(() => import("./components/AboutUs"));
const AboutUsMKD = lazy(() => import("./components/AboutUsMKD"));
const AboutUsNL = lazy(() => import("./components/AboutUsNL"));
const Contact = lazy(() => import("./components/Contact"));
const ContactMKD = lazy(() => import("./components/ContactMKD"));
const ContactNL = lazy(() => import("./components/ContactNL"));
const Contact2 = lazy(() => import("./components/cont"));
const FAQ = lazy(() => import("./components/FAQ"));
const FAQNL = lazy(() => import("./components/FAQNL"));
const FAQMKD = lazy(() => import("./components/FAQMKD"));
const ReservationForm = lazy(() => import("./components/ReservationForm"));
const ReservationFormNL = lazy(() => import("./components/ReservationFormNL"));
const ReservationFormMKD = lazy(() => import("./components/ReservationFormMKD"));

// Scroll to top component
function ScrollToTop() {
    const { pathname } = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    return null;
}

// Home page component
const HomePage = () => (
    <main>
        <Hero />
        <AtmosphereSection />
        <Testimonials />
        <Reservation />
    </main>
);

const HomePageMKD = () => (
    <main>
        <HeroMKD />
        <AtmosphereSectionMKD />
        <TestimonialsMKD />
        <ReservationMKD/>
    </main>
);


const HomePageNL = () => (
    <main>
        <HeroNL />
        <AtmosphereSectionNL />
        <TestimonialsNL />
        <ReservationNL/>
    </main>
);


// Page wrapper for consistent layout
const PageWrapper = ({ children, className = "" }) => (
    <main className={`page-wrapper ${className}`}>
        {children}
    </main>
);

// Loading component
const LoadingComponent = () => (
    <div className="loading-container">
        <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading...</p>
        </div>
    </div>
);


function App() {

    // App.jsx
    const [language, setLanguage] = useState('en');

    useEffect(() => {
        const storedLang = localStorage.getItem('language');
        if (storedLang) setLanguage(storedLang);
    }, []);

    const handleLanguageChange = (lang) => {
        setLanguage(lang);
        localStorage.setItem('language', lang);
    };

    useEffect(() => {
        // Add smooth scrolling behavior
        document.documentElement.style.scrollBehavior = 'smooth';

        // Cleanup
        return () => {
            document.documentElement.style.scrollBehavior = 'auto';
        };
    }, []);

    return (
        <ErrorBoundary>
            <Router>
                <div className="App">
                    <ScrollToTop />
                    {language === 'en' && <Navbar />}
                    {language === 'mk' && <NavbarMKD />}
                    {language === 'nl' && <NavbarNL />}

                    <div className="main-content">
                        <Suspense fallback={<LoadingComponent />}>
                            <Routes>
                                {/* Home Page */}
                                <Route
                                    path="/"
                                    element={
                                        <PageWrapper className="home-page">
                                            {language === 'en' && <HomePage />}
                                            {language === 'mk' && <HomePageMKD />}
                                            {language === 'nl' && <HomePageNL />}
                                        </PageWrapper>
                                    }
                                />

                                {/* Menu Page */}
                                <Route
                                    path="/menu"
                                    element={
                                        <PageWrapper className="menu-page">
                                            {language === 'en' && <ResMenu />}
                                            {language === 'mk' && <ResMenuMKD />}
                                            {language === 'nl' && <ResMenuNL />}
                                        </PageWrapper>
                                    }
                                />

                                {/* Reservation Page */}
                                <Route
                                    path="/reservation"
                                    element={
                                        <PageWrapper className="reservation-page">
                                            {language === 'en' && <ReservationForm />}
                                            {language === 'mk' && <ReservationFormMKD />}
                                            {language === 'nl' && <ReservationFormNL />}
                                        </PageWrapper>
                                    }
                                />

                                {/* About Us Page */}
                                <Route
                                    path="/about"
                                    element={
                                        <PageWrapper className="about-page">
                                            {language === 'en' && <AboutUs />}
                                            {language === 'mk' && <AboutUsMKD />}
                                            {language === 'nl' && <AboutUsNL />}
                                        </PageWrapper>
                                    }
                                />

                                {/* Contact Page */}
                                <Route
                                    path="/contact"
                                    element={
                                        <PageWrapper className="contact-page">
                                            {language === 'en' && <Contact />}
                                            {language === 'mk' && <ContactMKD />}
                                            {language === 'nl' && <ContactNL />}
                                        </PageWrapper>
                                    }
                                />

                                {/* FAQ Page */}
                                <Route
                                    path="/faq"
                                    element={
                                        <PageWrapper className="faq-page">
                                            {language === 'en' && <FAQ />}
                                            {language === 'mk' && <FAQMKD />}
                                            {language === 'nl' && <FAQNL />}
                                        </PageWrapper>
                                    }
                                />

                                {/* Events Page */}
                                <Route
                                    path="/events"
                                    element={
                                        <PageWrapper className="events-page">
                                            {language === 'en' && <EventPost />}
                                            {language === 'mk' && <EventPostMKD />}
                                            {language === 'nl' && <EventPostNL />}
                                        </PageWrapper>
                                    }
                                />

                                {/* 404 Page */}
                                <Route
                                    path="*"
                                    element={
                                        <PageWrapper className="not-found-page">
                                            <div className="not-found">
                                                <h1>404</h1>
                                                <h2>Page Not Found</h2>
                                                <p>The page you're looking for doesn't exist.</p>
                                                <a href="/" className="back-home-btn">
                                                    Return Home
                                                </a>
                                            </div>
                                        </PageWrapper>
                                    }
                                />
                            </Routes>
                        </Suspense>
                    </div>
                    {language === 'en' && <Footer />}
                    {language === 'mk' && <FooterMKD />}
                    {language === 'nl' && <FooterNL />}
                </div>
            </Router>
        </ErrorBoundary>
    );
}

export default App;